import { Component, inject, viewChild, ViewContainerRef, ChangeDetectionStrategy } from '@angular/core';
import { EventType, Router, RouterOutlet } from '@angular/router';
import { NotificationsContainerComponent } from "./components/notification/notifications-container/notifications-container.component";
import { PageLoadingBarComponent } from "./components/page-loading-bar/page-loading-bar.component";
import { ConfirmationService } from './services/confirmation.service';
import { ConfirmationComponent } from '@components/confirmation/confirmation.component';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, NotificationsContainerComponent, PageLoadingBarComponent],
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './app.component.scss'
})
export class AppComponent {
  private readonly Router = inject(Router);
  private readonly ConfirmationService = inject(ConfirmationService);

  public ConfirmationsContainer = viewChild.required("confirmationsContainer", { read: ViewContainerRef });
  public ModalsContainer = viewChild.required("modalsContainer", { read: ViewContainerRef });

  public constructor() {
    this.Router.events.subscribe({
      next: routerEvent => {
        if (routerEvent.type === EventType.NavigationEnd) {
          this.EnforceNoOverflowHiddenOnNavigationEnd();
        }
      }
    });

    this.ConfirmationService.Confirmations.subscribe({
      next: newConfirmation => {
        // A new confirmation data set has been added
        // Time to render it
        const component = this.ConfirmationsContainer().createComponent(ConfirmationComponent);
        component.setInput("Title", newConfirmation.Title);
        component.setInput("Body", newConfirmation.Body);
        component.setInput("ConfirmButtonText", newConfirmation.ConfirmText);
        component.setInput("RejectButtonText", newConfirmation.RejectText);

        // Destory the component when the modal is closed
        // Otherwise, it just hangs around in the DOM and is a nuisance
        component.instance.OnClosed.subscribe(() => {
          component.destroy();
        });

        component.instance.OnCancelButtonClicked.subscribe(() => {
          component.instance.Close();
        });

        component.instance.OnConfirmButtonClicked.subscribe(() => {
          if (newConfirmation.OnConfirm !== undefined) {
            newConfirmation.OnConfirm();
          }
        });

        // Tell the confirmation service that the confirmation component has been created
        this.ConfirmationService.NewConfirmationComponentCreatedSubscriber.next(component)
      }
    });
  }

  /**
   * When routing finished, enforce that no overflow hiddens from previous component
   * logic remains - preventing the user from scrolling
   */
  public EnforceNoOverflowHiddenOnNavigationEnd(): void {
    if (document !== null) {
      if (document.body !== null) {
        if (document.body.style.overflow === "hidden") {
          document.body.style.overflow = null!;
        }
      }
    }
  }
}